import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, MapPin, Building2, FlaskConical, ArrowUpRight, Mail, Loader2, RefreshCw } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { CONDITIONS } from "../data";
import { notify } from "../notify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusStyle = {
  Recruiting: "bg-[#eafaf1] text-[#22a35a]",
  "Not yet recruiting": "bg-[#fdf2e8] text-[#e08a2b]",
  "Enrolling by invitation": "bg-[#eef1fb] text-[#2b7fd9]",
  "Active, not recruiting": "bg-slate-100 text-slate-500",
};

export default function Trials() {
  const [condition, setCondition] = useState("Rheumatoid Arthritis");
  const [ukOnly, setUkOnly] = useState(true);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState({});

  const search = useCallback(async (cond) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/trials/search`, {
        params: { condition: cond, country: ukOnly ? "United Kingdom" : "", status: "RECRUITING" },
      });
      setTrials(data.trials || []);
      if (!(data.trials || []).length) toast.message("No recruiting trials found — try a broader condition or turn off UK-only.");
    } catch { toast.error("Could not load live trials. Please try again."); }
    setLoading(false);
  }, [ukOnly]);

  // Auto-search on mount and when the UK-only toggle changes (not on every keystroke).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { search(condition); }, [ukOnly]);

  const express = (t) => {
    setApplied((p) => ({ ...p, [t.id]: true }));
    notify("trial_interest", `Patient expressed interest in trial ${t.id} — "${t.title}" (${t.sponsor}, ${t.location}).`);
    toast.success(`Interest registered for ${t.id}`);
  };

  return (
    <AppLayout title="Clinical Trials">
      <div className="bg-gradient-to-r from-[#0d9488] to-[#0f766e] rounded-2xl p-6 text-white flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center"><FlaskConical size={26} /></div>
        <div>
          <h2 className="font-head font-bold text-xl">Live clinical trials</h2>
          <p className="text-white/85 text-sm mt-0.5">Real-time recruiting studies from ClinicalTrials.gov. Participation is always your choice.</p>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); search(condition); }} className="flex flex-col sm:flex-row gap-3 my-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input data-testid="trials-search-input" list="cond-list" value={condition} onChange={(e) => setCondition(e.target.value)} placeholder="Search by condition (e.g. Lupus, Crohn's)…" className="w-full h-11 pl-10 pr-4 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488] bg-white" />
          <datalist id="cond-list">{CONDITIONS.map((c) => <option key={c.name} value={c.name} />)}</datalist>
        </div>
        <button data-testid="trials-search-btn" type="submit" className="h-11 px-5 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold text-sm inline-flex items-center justify-center gap-2">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Search
        </button>
        <label className="flex items-center gap-2 text-[13px] font-semibold text-slate-600 shrink-0 px-1">
          <input data-testid="trials-uk-toggle" type="checkbox" checked={ukOnly} onChange={(e) => setUkOnly(e.target.checked)} className="w-4 h-4 accent-[#0d9488]" /> UK only
        </label>
      </form>

      {loading ? (
        <div className="p-12 text-center"><Loader2 className="animate-spin text-[#0d9488] mx-auto" /></div>
      ) : (
        <div className="space-y-4" data-testid="trials-list">
          {trials.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusStyle[t.status] || "bg-slate-100 text-slate-500"}`}>{t.status}</span>
                {t.phase && t.phase !== "N/A" && <span className="text-[11px] font-semibold text-[#0f766e] bg-[#e6f4f2] px-2 py-0.5 rounded-full">{t.phase}</span>}
                <span className="text-[11px] text-slate-400">{t.id}</span>
              </div>
              <h3 className="font-head font-semibold text-slate-800 mt-2">{t.title}</h3>
              <div className="flex items-center gap-4 mt-1.5 text-[13px] text-slate-500 flex-wrap">
                <span className="inline-flex items-center gap-1"><Building2 size={14} /> {t.sponsor}</span>
                <span className="inline-flex items-center gap-1"><MapPin size={14} /> {t.location}</span>
              </div>
              {t.conditions?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {t.conditions.slice(0, 4).map((c, i) => <span key={`${c}-${i}`} className="text-[12px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{c}</span>)}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <a data-testid={`trial-view-${t.id}`} href={t.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-slate-200 text-slate-700 font-semibold text-sm hover:border-[#0d9488]">View official listing <ArrowUpRight size={15} /></a>
                <button onClick={() => express(t)} disabled={applied[t.id]} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:bg-[#eafaf1] disabled:text-[#22a35a] text-white font-semibold text-sm transition-colors">{applied[t.id] ? "Interest sent" : "Express interest"}</button>
              </div>
            </div>
          ))}
          {!trials.length && <p className="text-slate-400 text-sm">No recruiting trials found. Try a different condition.</p>}
        </div>
      )}

      <p className="text-[12px] text-slate-400 flex items-center gap-1.5 mt-6"><Mail size={13} /> Live data from ClinicalTrials.gov. Always confirm eligibility with the research site. Not medical advice.</p>
    </AppLayout>
  );
}
